import { formatFilterLabel } from "@/features/sub-families/utils/formatters.ts";
import { useTranslations } from "@/features/translations/components/translations.tsx";
import useCurrentAccessory from "@/services/product/hooks/use-current-accessory.ts";
import useCurrentBeamAngle from "@/services/product/hooks/use-current-beam-angle.ts";
import useCurrentCRI from "@/services/product/hooks/use-current-cri.ts";
import useCurrentMountingKit from "@/services/product/hooks/use-current-mounting-kit.ts";
import useCurrentProduct from "@/services/product/hooks/use-current-product.ts";
import { type ProductPropertyKey } from "../types";

export default function useGetSelectedOptionLabel() {
    const t = useTranslations();

    const currentProduct = useCurrentProduct();
    const accessory = useCurrentAccessory();

    const mountingKit = useCurrentMountingKit();
    const cri = useCurrentCRI();
    const beamAngle = useCurrentBeamAngle();

    return (property: ProductPropertyKey) => {
        if (!currentProduct) return null;

        switch (property) {
            case "ceiling_meta_data": {
                return mountingKit?.ceilingLabel ?? t("no_mounting_kit");
            }

            case "light_acc": {
                return accessory?.title ?? null;
            }

            case "cri": {
                return cri ? formatFilterLabel(cri) : null;
            }

            case "beam_angle": {
                return beamAngle ? formatFilterLabel(beamAngle) : null;
            }

            case "cct": {
                return formatFilterLabel(currentProduct.properties.cct_type);
            }

            // These cases should not have a filter
            case "power_supply": // handled by their own hooks as these are special cases
            case "dimming_type": // handled by their own hooks as these are special cases
            case "documents":
            case "images":
            case "supply": {
                return null;
            }

            default: {
                const propertyConfig = currentProduct.properties[property];
                if (!propertyConfig) return null;

                return formatFilterLabel(propertyConfig);
            }
        }
    };
}
